import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";

const DetailedCampaignReport = ({ startDate, endDate, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [pageSize, setPageSize] = useState(10);

  const handleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const getVisibleRows = (rows) => {
    if (!rows) return [];
    return rows.filter(
      (row) => row.parent_id === null || expandedRows.has(row.parent_id),
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/reports/CallCentreSalesReports/DetailedCampaignPEQ?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Transform the data
        const transformedData = [];
        let currentId = 1;

        response.data.data.forEach((team) => {
          // Add manager/team row
          const teamRow = {
            id: currentId,
            parent_id: null,
            manager: team.manager,
            leads_attempted: team.leads_attempted,
            leads_contacted: team.leads_contacted,
            touches: team.touches,
            campaign_hours: team.campaign_hours,
            contact_rate: (
              (team.leads_contacted / team.leads_attempted) *
              100
            ).toFixed(0),
            touches_per_hour: (team.touches / team.campaign_hours).toFixed(0),
          };

          transformedData.push(teamRow);

          // Add agent rows
          team.agent_team_details.forEach((agent) => {
            transformedData.push({
              ...agent,
              id: ++currentId,
              parent_id: teamRow.id,
              contact_rate: (
                (agent.leads_contacted / agent.leads_attempted) *
                100
              ).toFixed(0),
              touches_per_hour: (agent.touches / agent.campaign_hours).toFixed(
                0,
              ),
            });
          });

          currentId++;
        });

        setData(transformedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, token]);

  const columns = [
    {
      field: "__expand",
      headerName: "",
      width: 50,
      renderCell: (params) => {
        const hasChildren = data.some((row) => row.parent_id === params.row.id);
        if (!hasChildren) return null;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRowExpansion(params.row.id);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.2rem",
              color: "var(--mui-palette-secondary-main)",
            }}
          >
            {expandedRows.has(params.row.id) ? "−" : "+"}
          </button>
        );
      },
    },
    {
      field: "manager",
      headerName: "Manager/Agent",
      flex: 2,
      valueGetter: (params) => params.row.agent_name || params.row.manager,
    },
    {
      field: "leads_attempted",
      headerName: "Leads Attempted",
      flex: 1,
      type: "number",
    },
    {
      field: "leads_contacted",
      headerName: "Leads Contacted",
      flex: 1,
      type: "number",
    },
    {
      field: "contact_rate",
      headerName: "Leads Contacted %",
      flex: 1,
      type: "number",
    },
    {
      field: "touches",
      headerName: "Touches",
      flex: 1,
      type: "number",
    },
    // {
    //   field: "campaign_hours",
    //   headerName: "Campaign Hours",
    //   flex: 1,
    //   type: "number",
    //   valueFormatter: (params) => params.value.toFixed(2),
    // },
    {
      field: "touches_per_hour",
      headerName: "Calls per Hour",
      flex: 1,
      type: "number",
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data || data.length === 0) return <h3>No Data Available</h3>;

  return (
    <Box sx={{ height: 600, width: "100%", marginTop: 2 }}>
      <DataGridPremium
        rows={getVisibleRows(data)}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        // pagination
        slots={{ toolbar: GridToolbar }}
        // initialState={{
        //   pagination: { paginationModel: { pageSize: 10 } },
        // }}
        pageSizeOptions={[5, 10, 15, 20]}
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.row.parent_id ? "agent-row" : "manager-row"
        }
        sx={{
          "& .manager-row": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[100],
            fontWeight: "bold",
          },
          "& .agent-row": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.common.white,
          },
        }}
      />
    </Box>
  );
};

export default DetailedCampaignReport;
